"use client";

import { useRef, type ReactNode } from "react";
import { ReadingProgressBar } from "./reading-progress-bar";
import { TableOfContents } from "./table-of-contents";
import type { StockAnalysisHeading } from "@/lib/stock-analyses/headings";

/**
 * Umschliesst eine Aktienanalyse mit Fortschrittsbalken + Inhaltsverzeichnis
 * (Erik-Wunsch 2026-08-04). `children` bleibt normaler Server-Render-Output (Banner,
 * Ticker-Zeile, Markdown-Body, ggf. PremiumGate) - wird nur zur Hoehenmessung in einen
 * ref-tragenden Container gefasst, muss dafuer selbst keine Client-Komponente werden.
 *
 * Lesespalte auf max-w-[min(94vw,1300px)] verbreitert (Erik 2026-08-04: "Rand ist zu
 * gross, Bildschirm mehr ausnutzen" - vorher 1100px). Das Inhaltsverzeichnis erscheint
 * als sticky Sidebar ab `lg` (1024px) - eine fruehere Fassung nutzte min-[1700px], was
 * in der Praxis auf normal breiten Laptop-/Desktop-Fenstern (inkl. Windows-Skalierung
 * >100%, nicht maximierten Fenstern) so gut wie nie griff und Erik beim Testlauf
 * dadurch nur den <details>-Fallback ueber dem Text zu sehen bekam statt der Sidebar
 * (Erik 2026-08-04: "ich will das Inhaltsverzeichnis links neben der Analyse"). Bei
 * `lg` bleibt fuer die Lesespalte nicht immer die vollen 1300px Platz (max-w ist eine
 * Obergrenze, kein Fixwert) - das ist gewolltes, normales Reflow-Verhalten, kein Bug.
 */
export function AnalysisReaderShell({
  headings,
  children,
}: {
  headings: StockAnalysisHeading[];
  children: ReactNode;
}) {
  const articleRef = useRef<HTMLDivElement>(null);

  return (
    <div className="flex flex-col gap-6">
      <ReadingProgressBar articleRef={articleRef} />
      <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
        <TableOfContents headings={headings} />
        <div ref={articleRef} className="flex w-full max-w-[min(94vw,1300px)] flex-col gap-8">
          {children}
        </div>
      </div>
    </div>
  );
}
